/**
 * Trades a Descope session token for a Firebase custom token at
 * `POST /api/auth/descope`, without signing in yet.
 *
 * Split from the sign-in step so a caller coordinating two async auth systems
 * can decide, once the token is in hand, whether the Descope session it was
 * minted from is still the current one before applying it (see
 * `FirebaseSessionSync`).
 */
// Bound the exchange so a hung request cannot stall the reconciliation
// indefinitely: a pending fetch blocks the serialized chain (so a later
// sign-out could not run) and would leave the destination spinning. On
// timeout the fetch rejects, the caller records an error, and the UI offers a
// retry/sign-out.
const EXCHANGE_TIMEOUT_MS = 15000;

export async function fetchFirebaseCustomToken(
  sessionJwt: string,
): Promise<string> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/descope`,
      {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify({ token: sessionJwt }),
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      // Status only: the response body can carry backend detail that should
      // not surface in client logs or the UI.
      throw new Error(
        `POST /api/auth/descope failed with status ${response.status}`,
      );
    }

    const { token } = await response.json();
    if (typeof token !== 'string' || !token) {
      throw new Error(
        'POST /api/auth/descope returned no token in its response body',
      );
    }
    return token;
  } finally {
    clearTimeout(timer);
  }
}
